import React from "react";
import {
  NavLink,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { format, parseJSON } from "date-fns";
import titleize from "underscore.string/titleize";
import humanize from "underscore.string/humanize";
import endsWith from "underscore.string/endsWith";
import strLeft from "underscore.string/strLeft";
import {
  Layouts,
  Loaders,
  Buttons,
  AttributeValue,
  ResponsiveTable,
} from "./../components";
import { formatMoney } from "./../utils/currrency";
import { StatusBar, Document, InvestorsList } from "../components/offer";
import { OfferUpdateContextProvider } from "./../contexts/OfferUpdateContext";
import {
  useOffer,
  useOfferPitch,
} from "./../queries/offer";
import {DEAL_TYPES, OFFER_STATUSES} from "./../config/constants";
import OfferUpdates from "../components/offer/updates";
import LiveEvents from "../components/offer/live-events";
import { useSessionContext } from "./../contexts/SessionContext";
import { formatDate } from "./../utils/datetime"

export default function OfferOverview() {
  const navigate = useNavigate();
  const location = useLocation();
  let { offer_id } = useParams();
  const {data:offer, isLoading } = useOffer({offer_id});
  const {data:pitch, isLoading:isLoadingPitch} = useOfferPitch({offer_id});
  const { isPermitted, ACTIONS } = useSessionContext();

  const filledPitchSections = []

  for (const [key, value] of Object.entries(pitch)) {
    if(endsWith(key, "_section") && value != null){
      filledPitchSections.push({name: strLeft(key, "_section"), section: value})
    }
    else {
      continue;
    }
  }

  const columns = [
    {
      Header: 'Section name',
      accessor: 'name',
      Cell: ({value}) => value? humanize(value) : null
    },{
      Header: 'Last updated',
      accessor: 'section.updated_at',
      Cell: ({value}) => value ? formatDate(value, 'MMM dd, yyyy, hh:mm a') : null
    },
  ];

  const openEditorModal = React.useCallback(
    (row) => {
      navigate(
        "edit",
        {
          state: {
            backgroundLocation: location
          }
        }
      )
    },
    [navigate, location]
  )

  if(isLoading || isLoadingPitch)
    return <Loaders.DashboardLoader />

  return (
    <>
      <Layouts.PageHeader left={titleize(offer.name)} right={ <StatusBar offer={offer} /> } />
      <div className="overview-content">
        <div className="nav-tabs-container">
          <ul className="nav nav-tabs px-3" role="tablist">
            <li className="nav-item">
              <NavLink to="offer-overview" className="nav-link active" data-bs-toggle="tab" data-bs-target="#offer-overview" role="tab">Overview</NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="offer-investors" className="nav-link" data-bs-toggle="tab" data-bs-target="#offer-investors" role="tab">Investors</NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="offer-updates" className="nav-link" data-bs-toggle="tab" data-bs-target="#offer-updates" role="tab">Updates</NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="live-events" className="nav-link" data-bs-toggle="tab" data-bs-target="#live-events" role="tab">Live Events</NavLink>
            </li>
          </ul>
        </div>
        <div className="tab-content px-3">
          <div className="tab-pane fade show active" id="offer-overview" role="tabpanel">
            <Layouts.OverviewSubheader left='Offer Overview' tooltipDesc="All the core details about your Offer. Spaced Ventures sets this up and it is only viewable by Issuers." />
            <Layouts.TwoColumnLayout
              left={<AttributeValue name='Offer name' value={titleize(offer.name)} />}
              right={<AttributeValue name='Company name' value={titleize(offer.company.name)} />}
            />
            <Layouts.TwoColumnLayout
              left={
                <>
                  <AttributeValue name='Deal type' value={offer.deal?.type} />
                  {offer?.deal?.type === DEAL_TYPES.DEBT && <>
                    <AttributeValue name='Discount' value={offer.deal?.discount} />
                    <AttributeValue name='Valuation cap' value={formatMoney(offer.deal?.valuation_cap)} />
                  </>}
                  {offer?.deal?.type === DEAL_TYPES.EQUITY && <AttributeValue name='Valuation' value={formatMoney(offer.deal?.valuation)} />}
                </>
              }
              right={
                <>
                  {offer?.deal?.type === DEAL_TYPES.DEBT && <>
                      <AttributeValue name='Type of security' value={humanize(offer.deal?.security_type)} />
                      <AttributeValue name='Maturity date' value={offer?.deal?.maturity_date ? format(parseJSON(new Date(offer?.deal?.maturity_date)), 'MMM dd, yyyy') : "-"} />
                      <AttributeValue name='Interest rate' value={offer?.deal?.interest_rate ? `${offer?.deal?.interest_rate}%` : "-"} />
                    </>}
                  {offer?.deal?.type === DEAL_TYPES.EQUITY && <>
                    <AttributeValue name='Price per share' value={formatMoney(offer.deal?.price_per_share)} />
                    <AttributeValue name='Share type' value={titleize(offer.deal?.share_type)} />
                  </>}
                </>
              }
            />

            <Layouts.TwoColumnLayout
              left={
                <>
                  <AttributeValue name='Offer minimum goal' value={formatMoney(offer.deal?.minimum_goal)} />
                  <AttributeValue name='Offer target goal' value={formatMoney(offer.deal?.goal)} />
                  <AttributeValue name='Offer maximum goal' value={formatMoney(offer.deal?.maximum_goal)} />
                </>
              }
              right={
                <>
                  <AttributeValue name='Closing date' value={ offer.deal?.close_date ? `${ format(parseJSON(new Date(offer.deal?.close_date)), 'MMM dd, yyyy, h:mm a') } userTimezone` : '-'} />
                  <AttributeValue name='Investment minimum' value={formatMoney(offer.deal?.minimum_investment)} />
                  <AttributeValue name='Investment maximum' value={formatMoney(offer.deal?.maximum_investment)} />
                </>
              }
            />

            <Layouts.OverviewSubheader
              left='Pitch Sections'
              right={
                offer.status !== OFFER_STATUSES.LIVE && isPermitted(ACTIONS.WRITE_UPDATE_PITCH, offer.company.id)
                  ? <Buttons.TextButton title='Edit' onClick={openEditorModal} />
                  : null
              }
              tooltipDesc="All the pitch sections for your Offer will be listed below and when they were last updated. Pitch sections are editable by Issuers up until the Offer is Live."
            />
            <ResponsiveTable title="Pitch Sections" columns={columns} data={filledPitchSections}/>

            <Layouts.OverviewSubheader left='Documents' tooltipDesc="The Documents available for investors to download. Documents are added by Spaced Ventures during the offer building process." />
            <div className="two-col-layout">
              <div className="documents-box w-100 pb-4">
                <Document href={offer.formc_url} name="Form C" />
                <Document href={offer.security_document?.attachment_url} name="Security Document" />
                {
                  offer.other_documents.map(doc => <Document href={doc.attachment_url} name={titleize(doc.name)} />)
                }
              </div>
            </div>
          </div>

          <div className="tab-pane fade show" id="offer-investors" role="tabpanel">
            <Layouts.OverviewSubheader left='Performance' tooltipDesc="The performance of your offer. Includes the total amount raised and the number of unique investors." />
            <div className="two-col-layout">
              <div className="first-col p-3">
                <AttributeValue
                  name={
                    <>
                      <span>Raised:</span>
                      <span style={{fontWeight: 'bold', marginLeft: '5px'}}>{
                        `${formatMoney(offer?.deal?.raised || 0, {precision : 0})} / ${formatMoney(offer?.deal?.maximum_goal || 0, {precision : 0})}`
                        }</span>
                    </>
                  }
                  value={
                    <div style={{ width: '300px', backgroundColor: '#fff'}}>
                      <div style={{width: `${Math.min(Math.max(offer?.deal?.raised*100 / offer?.deal?.maximum_goal, 0), 100) || 0}%`, height: '18px', backgroundColor: '#5AB4AD'}} />
                    </div>
                  }
                />
              </div>
              <div className="second-col p-3">
                <AttributeValue name='Number of investors' value={offer.investor_count || 0} />
              </div>
            </div>
            <InvestorsList companyId={offer.company.id} offerId={offer_id} />
          </div>
          <div className="tab-pane fade show" id="offer-updates" role="tabpanel">
            <div className="row">
              <OfferUpdateContextProvider>
                <div className="col-12 col-md-12">
                  <OfferUpdates.List
                    offerId={offer.slug}
                    canEdit={
                      (
                        offer.status === OFFER_STATUSES.LIVE ||
                        offer.status === OFFER_STATUSES.CLOSED ||
                        offer.status === OFFER_STATUSES.PAUSED
                      ) && isPermitted(ACTIONS.WRITE_UPDATE_OFFER_UPDATE, offer.company.id)
                    }
                  />
                </div>
              </OfferUpdateContextProvider>
            </div>
          </div>
          <div className="tab-pane fade show" id="live-events" role="tabpanel">
            <div className="row">
              <div className="col-12">
                <LiveEvents.List companyId={offer.company.id} offerId={offer.id} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
